import React from "react"
import styled, {keyframes} from "styled-components";
import {Project, ProjectType} from "../../consts/projects";
import TabletMockup from "../Mockups/TabletMockup";
import PhoneMockup from "../Mockups/PhoneMockup";
import {Link} from "react-router-dom";

interface Props {
    project: Project
}

const WelcomeBanner = (props: Props) => {
    return (
        <Hero>
            <HeroContent>
                <Label style={{animationDelay: '0.1s'}}>Web developer</Label>
                <MainTitle style={{animationDelay: '0.2s'}}>
                    Systémy, webové<br/>stránky & aplikace
                </MainTitle>
                <Text style={{animationDelay: '0.3s'}}>
                    Pojďme najít řešení i pro Váš projekt. Od prvního nápadu
                    po funkční produkt — rychle, moderně a na míru.
                </Text>
                <Buttons style={{animationDelay: '0.4s'}}>
                    <Link to="/sluzby/"><Btn>Služby</Btn></Link>
                    <Link to="/reference/"><SecondaryBtn>Reference</SecondaryBtn></Link>
                </Buttons>
            </HeroContent>

            <HeroVisual style={{animationDelay: '0.3s'}}>
                <MockupGlow />
                {props.project.type === ProjectType.TABLET ? (
                    <TabletMockup project={props.project}/>
                ) : (
                    <PhoneMockup project={props.project}/>
                )}
            </HeroVisual>
        </Hero>
    )
}

export default WelcomeBanner

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const Hero = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  min-height: calc(100vh - 10em);
  gap: 3em;
  padding-top: 5em;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    min-height: auto;
    padding-top: 3em;
    gap: 2em;
  }
`

const HeroContent = styled.div`
  @media (max-width: 1100px) {
    max-width: 36em;
    margin: 0 auto;
    text-align: center;
  }
`

const Label = styled.div`
  font-size: 0.78em;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.3;
  margin-bottom: 1.8em;
  animation: ${fadeUp} 0.6s ease both;
`

const MainTitle = styled.h1`
  font-weight: 900;
  font-size: 3.8em;
  line-height: 1.15;
  margin: 0;
  letter-spacing: -0.03em;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 1300px) {
    font-size: 3.2em;
  }

  @media (max-width: 500px) {
    font-size: 2.4em;
  }
`

const Text = styled.p`
  margin: 1.8em 0 2.2em;
  max-width: 26em;
  line-height: 1.75;
  opacity: 0.5;
  font-weight: 300;
  font-size: 1.05em;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 1100px) {
    margin-left: auto;
    margin-right: auto;
  }
`

const Buttons = styled.div`
  display: flex;
  gap: 0.6em;
  animation: ${fadeUp} 0.6s ease both;

  a {
    text-decoration: none;
  }

  @media (max-width: 1100px) {
    justify-content: center;
  }
`

export const Btn = styled.button`
  background-color: rgba(25, 25, 25, 1);
  color: white;
  border: none;
  padding: 0.8em 1.6em;
  font-size: 0.95em;
  font-weight: 600;
  font-family: inherit;
  outline: none;
  border-radius: 0.4em;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
  letter-spacing: 0.01em;

  &:hover {
    transform: translateY(-2px);
    background: rgba(25, 25, 25, 0.85);
  }
`

export const SecondaryBtn = styled(Btn)`
  background-color: #ebebeb;
  color: rgba(25, 25, 25, 1);

  &:hover {
    background: #e0e0e0;
  }
`

const HeroVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8em;
  animation: ${fadeIn} 0.8s ease both;

  @media (max-width: 1300px) {
    font-size: 0.65em;
  }

  @media (max-width: 1100px) {
    font-size: 0.7em;
    margin-bottom: 2em;
  }

  @media (max-width: 600px) {
    font-size: 0.55em;
  }

  @media (max-width: 450px) {
    font-size: 0.45em;
  }
`

const MockupGlow = styled.div`
  position: absolute;
  width: 70%;
  height: 70%;
  background: radial-gradient(circle, rgba(0, 0, 0, 0.04) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
`
